import type { HTMLAttributes } from 'react';
import { useIntl } from 'react-intl';

import { Rarity, Sport } from '#src/__generated__/globalTypes.ts';
import { FRONTEND_ASSET_HOST } from '#src/constants/assets.ts';
import { getScarcityName } from '#src/lib/baseball.ts';

import { DumbCardBack } from './DumbCardBack.tsx';

type Props = {
  path?: string;
  sport?: Sport;
  tier?: number;
  rarity: Rarity;
  season?: string;
  radius?: string;
  shine?: boolean;
} & HTMLAttributes<HTMLElement>;

const pathBySport = {
  [Sport.BASEBALL]: 'mlb',
  [Sport.NBA]: 'nba',
};

const getCardBackUrl = (
  sport: Sport | undefined,
  { rarity, season, tier }: { rarity: Rarity; season?: string; tier?: number }
) => {
  if (sport === Sport.BASEBALL || sport === Sport.NBA) {
    const urlParts = [FRONTEND_ASSET_HOST, 'cards/back', pathBySport[sport]];
    if (season && season !== '2022') {
      urlParts.push(season);
    }
    urlParts.push(`${rarity}_tier_${tier === undefined ? 'none' : tier}.png`);
    return urlParts.join('/');
  }
  if (sport === Sport.FOOTBALL) {
    const tokens: string[] = [Rarity[rarity]];
    if (tier !== undefined) {
      tokens.push(`tier_${tier}`);
    }
    return `${FRONTEND_ASSET_HOST}/cards/back/${tokens.join('-')}.svg`;
  }

  return `${FRONTEND_ASSET_HOST}/cards/back/generic/${rarity || Rarity.common}.png`;
};

export const CardBack = ({
  path: forcedPath,
  sport,
  rarity,
  tier,
  season,
  shine,
  radius,
  ...props
}: Props) => {
  const { formatMessage } = useIntl();

  const ariaLabel =
    tier !== undefined
      ? formatMessage(
          {
            id: 'CardBack.titleWithTier',
            defaultMessage: '{scarcity} Card, tier {tier}',
          },
          { scarcity: getScarcityName(rarity), tier }
        )
      : formatMessage(
          {
            id: 'CardBack.title',
            defaultMessage: '{scarcity} Card',
          },
          { scarcity: getScarcityName(rarity) }
        );

  const path = forcedPath ?? getCardBackUrl(sport, { rarity, season, tier });

  return (
    <DumbCardBack
      path={path}
      aria-label={ariaLabel}
      shine={shine}
      radius={radius}
      {...props}
    />
  );
};
